// src/hooks/useFirestore.ts
import { useState, useCallback } from "react";
import { firestoreService } from "../api/firebase/firestore";
import {
  usersCollection,
  activitiesCollection,
} from "../api/firebase/firestore";
import { User, Activity } from "../types";
import { convertTimestamp } from "../utils/helpers";

export const useFirestore = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users operations with pagination
  const getUsers = useCallback(
    async (
      page: number = 1,
      pageSize: number = 10,
    ): Promise<{ users: User[]; total: number; totalPages: number }> => {
      setLoading(true);
      setError(null);
      try {
        const result = await firestoreService.getPaginated<User>(
          usersCollection,
          page,
          pageSize,
          { field: "created_at", direction: "desc" },
        );

        const users = result.data.map((user) => ({
          ...user,
          created_at: convertTimestamp(user.created_at),
          updated_at: convertTimestamp(user.updated_at),
        }));

        return {
          users,
          total: result.total,
          totalPages: result.totalPages,
        };
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Search users with pagination
  const searchUsers = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      pageSize: number = 10,
    ): Promise<{ users: User[]; total: number; totalPages: number }> => {
      setLoading(true);
      setError(null);
      try {
        const result = await firestoreService.searchPaginated<User>(
          usersCollection,
          searchTerm,
          page,
          pageSize,
          ["name", "email", "username"],
        );

        const users = result.data.map((user) => ({
          ...user,
          created_at: convertTimestamp(user.created_at),
          updated_at: convertTimestamp(user.updated_at),
        }));

        return {
          users,
          total: result.total,
          totalPages: result.totalPages,
        };
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createUser = useCallback(
    async (userData: Omit<User, "id">): Promise<string> => {
      setLoading(true);
      setError(null);
      try {
        const userId = await firestoreService.create<User>(
          usersCollection,
          userData,
        );

        // Create activity log
        await firestoreService.create<Omit<Activity, "id">>(
          activitiesCollection,
          {
            type: "user_created",
            description: `User ${userData.name} created`,
            userName: userData.name,
            timestamp: new Date(),
          },
        );

        return userId;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateUser = useCallback(
    async (id: string, userData: Partial<User>): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await firestoreService.update<User>(usersCollection, id, userData);

        // Create activity log
        await firestoreService.create<Omit<Activity, "id">>(
          activitiesCollection,
          {
            type: "user_updated",
            description: `User ${userData.name || "updated"}`,
            userId: id,
            timestamp: new Date(),
          },
        );
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteUser = useCallback(
    async (id: string, userName: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await firestoreService.delete(usersCollection, id);

        // Create activity log
        await firestoreService.create<Omit<Activity, "id">>(
          activitiesCollection,
          {
            type: "user_deleted",
            description: `User ${userName} deleted`,
            userName,
            timestamp: new Date(),
          },
        );
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    getUsers,
    searchUsers,
    createUser,
    updateUser,
    deleteUser,
  };
};
